/**
 * Cost Modeling Page
 * Integrates the CostOptimizationModeler component for comprehensive cost analysis
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AttachMoney,
  TrendingUp,
  TrendingDown,
  Assessment,
  Calculate,
  CompareArrows,
  Save,
  History,
  Delete,
  Edit,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { CostOptimizationModeler } from '../components/optimization/CostOptimizationModeler';
import { useProject } from '../contexts/ProjectContext';
import { format } from 'date-fns';

interface SavedCostModel {
  id: string;
  name: string;
  description: string;
  createdAt: Date;
  totalCost: number;
  savings?: number;
  categories: any[];
}

export const CostModeling: React.FC = () => {
  const { currentProject } = useProject();
  const [savedModels, setSavedModels] = useState<SavedCostModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<SavedCostModel | null>(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareModels, setCompareModels] = useState<string[]>([]);

  // Simulated cost metrics
  const costMetrics = {
    currentMonthCost: 125000,
    lastMonthCost: 132000,
    yearToDateCost: 1450000,
    projectedAnnualCost: 1740000,
    costTrend: -5.3, // Percentage change
    savingsOpportunity: 87000,
  };

  const handleSaveModel = (model: any) => {
    const newModel: SavedCostModel = {
      ...model,
      createdAt: new Date(),
      savings: Math.floor(Math.random() * 50000) + 10000, // Simulated savings
    };
    setSavedModels(prev => [...prev, newModel]);
  };

  const handleDeleteModel = (modelId: string) => {
    setSavedModels(prev => prev.filter(m => m.id !== modelId));
  };

  const handleCompareModels = () => {
    if (compareModels.length < 2) {
      alert('Please select at least 2 models to compare');
      return;
    }
    console.log('Comparing models:', compareModels);
    // Implementation for model comparison
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Cost Modeling & Analysis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Optimize delivery costs and analyze financial performance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<History />}
            onClick={() => setHistoryDialogOpen(true)}
            size="small"
          >
            Model History
          </Button>
          <Button
            variant="outlined"
            startIcon={<CompareArrows />}
            onClick={() => setCompareMode(!compareMode)}
            color={compareMode ? 'primary' : 'inherit'}
            size="small"
          >
            Compare Models
          </Button>
        </Box>
      </Box>

      {/* Cost Metrics Dashboard */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Current Month Cost
              </Typography>
              <Typography variant="h4" fontWeight={600}>
                ${costMetrics.currentMonthCost.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {costMetrics.costTrend < 0 ? (
                  <>
                    <TrendingDown sx={{ color: 'success.main', mr: 0.5 }} />
                    <Typography variant="body2" color="success.main">
                      {Math.abs(costMetrics.costTrend)}% vs last month
                    </Typography>
                  </>
                ) : (
                  <>
                    <TrendingUp sx={{ color: 'error.main', mr: 0.5 }} />
                    <Typography variant="body2" color="error.main">
                      {costMetrics.costTrend}% vs last month
                    </Typography>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Year to Date
              </Typography>
              <Typography variant="h4" fontWeight={600}>
                ${costMetrics.yearToDateCost.toLocaleString()}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(costMetrics.yearToDateCost / costMetrics.projectedAnnualCost) * 100}
                sx={{ mt: 2, height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {((costMetrics.yearToDateCost / costMetrics.projectedAnnualCost) * 100).toFixed(1)}% of projected annual
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2" gutterBottom>
                Projected Annual
              </Typography>
              <Typography variant="h4" fontWeight={600}>
                ${costMetrics.projectedAnnualCost.toLocaleString()}
              </Typography>
              <Chip
                label="Based on current trends"
                size="small"
                sx={{ mt: 1 }}
                icon={<Assessment />}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: 'white', opacity: 0.9 }} gutterBottom>
                Savings Opportunity
              </Typography>
              <Typography variant="h4" fontWeight={600} sx={{ color: 'white' }}>
                ${costMetrics.savingsOpportunity.toLocaleString()}
              </Typography>
              <Button
                variant="contained"
                size="small"
                sx={{ 
                  mt: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
                startIcon={<Calculate />}
              >
                View Details
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Compare Mode Alert */}
      {compareMode && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleCompareModels}
              disabled={compareModels.length < 2}
            >
              Compare Selected ({compareModels.length})
            </Button>
          }
        >
          Select models from the history to compare their cost structures and savings potential.
        </Alert>
      )}

      {/* Cost Modeler */}
      <Paper elevation={2}>
        {!currentProject ? (
          <Alert severity="info">
            <AlertTitle>No Project Selected</AlertTitle>
            Please select a project from the Projects page to access cost modeling.
          </Alert>
        ) : (
          <CostOptimizationModeler
            onSave={handleSaveModel}
            initialScenario={selectedModel || undefined}
          />
        )}
      </Paper>

      {/* Model History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Saved Cost Models</Typography>
            <Typography variant="body2" color="text.secondary">
              {savedModels.length} models saved
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {savedModels.length === 0 ? (
            <Alert severity="info">
              No saved models yet. Save your first cost model to see it here.
            </Alert>
          ) : (
            <List>
              {savedModels.map((model) => (
                <ListItem
                  key={model.id}
                  sx={{
                    border: '1px solid #E5E7EB',
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: selectedModel?.id === model.id ? 'action.selected' : 'transparent',
                  }}
                >
                  {compareMode && (
                    <Checkbox
                      checked={compareModels.includes(model.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setCompareModels(prev => [...prev, model.id]);
                        } else {
                          setCompareModels(prev => prev.filter(id => id !== model.id));
                        }
                      }}
                      sx={{ mr: 2 }}
                    />
                  )}
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight={500}>
                          {model.name}
                        </Typography>
                        {model.savings && model.savings > 50000 && (
                          <Chip
                            label="High Savings"
                            size="small"
                            color="success"
                            icon={<CheckCircle />}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {model.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Created: {format(model.createdAt, 'MMM d, yyyy')}
                          </Typography>
                          <Typography variant="caption" color="primary">
                            Total Cost: ${model.totalCost.toLocaleString()}
                          </Typography>
                          {model.savings && (
                            <Typography variant="caption" color="success.main">
                              Savings: ${model.savings.toLocaleString()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Load Model">
                      <IconButton
                        onClick={() => {
                          setSelectedModel(model);
                          setHistoryDialogOpen(false);
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Model">
                      <IconButton
                        onClick={() => handleDeleteModel(model.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Add missing import
import { Checkbox } from '@mui/material';